import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Dialog } from "primereact/dialog";
import { Divider } from "primereact/divider";
import { FloatLabel } from "primereact/floatlabel";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { TabMenu } from "primereact/tabmenu";
import ConfirmationWrapper from "../common/ConfirmationWrapper";
import Loading from "../common/Loading";
import NoContent from "../common/NoContent";
import PageTitle from "../common/PageTitle";
import ProgressiveControl from "../common/ProgressiveControl";
import TabHeader from "../common/TabHeader";
import { useAppContext } from "../../providers/ProviderAppContainer";
import { TEXT_NORMAL, TEXT_SMALL } from "../../style";
import FieldNote from "./common/FieldNote";
import WebsiteMediaInput from "./WebsiteMediaInput";

export default function WebsiteGalleryEvent() {
    const { slug, albumId } = useParams();
    const { requestAPI, showToast } = useAppContext();
    const [loading, setLoading] = useState();
    const [error, setError] = useState();
    const [album, setAlbum] = useState();
    const [rows, setRows] = useState();
    const [tab, setTab] = useState("image");
    const [dialog, setDialog] = useState({ visible: false });

    const loadRows = useCallback(() => {
        requestAPI({
            requestPath: "website/gallery-albums",
            requestMethod: "GET",
            setLoading,
            onRequestFailure: setError,
            onResponseReceieved: (albums, responseCode) => {
                if (!albums || responseCode !== 200) {
                    setError("Couldn't load this event");
                    return;
                }
                const match = albums.find((item) => String(item.id) === String(albumId));
                if (!match) {
                    setError("This event was not found");
                    return;
                }
                setAlbum(match);
                requestAPI({
                    requestPath: "website/gallery-media",
                    requestMethod: "GET",
                    onRequestFailure: setError,
                    onResponseReceieved: (media, mediaCode) => {
                        if (media && mediaCode === 200) setRows(media.filter((item) => item.album_public_id === match.public_id));
                        else setError("Couldn't load photos and videos");
                    },
                });
            },
        });
    }, [albumId, requestAPI]);

    useEffect(() => {
        loadRows();
    }, [loadRows]);

    const visible = (rows || []).filter((item) => (tab === "video" ? item.media_type === "video" : item.media_type !== "video"));

    return (
        <div className="flex flex-column h-full">
            <PageTitle title={album?.title || "Event"} />
            <TabHeader
                className="px-3 pt-3"
                title={album?.title || "Event"}
                highlights={["Photos and videos for this event. The website shows them on separate tabs"]}
                actionItems={[
                    <Button icon="pi pi-plus" severity="warning" onClick={() => setDialog({ visible: true, record: { album_public_id: album?.public_id, media_type: tab, downloadable: false } })} />,
                ]}
            />
            <TabMenu
                model={[
                    { label: "Photos", icon: "pi pi-image", command: () => setTab("image") },
                    { label: "Videos", icon: "pi pi-video", command: () => setTab("video") },
                ]}
                activeIndex={tab === "video" ? 1 : 0}
            />
            <Divider />
            {loading ? <Loading /> : error ? <NoContent error={error} /> : (
                <div className="flex-1 overflow-y-scroll flex flex-column gap-2 p-2">
                    {visible.length ? visible.map((row) => (
                        <Row key={row.id} row={row} setRows={setRows} requestAPI={requestAPI} showToast={showToast} openEdit={(record) => setDialog({ visible: true, record })} />
                    )) : <NoContent />}
                </div>
            )}
            {dialog.visible ? <Editor dialog={dialog} setDialog={setDialog} setRows={setRows} requestAPI={requestAPI} showToast={showToast} /> : null}
        </div>
    );
}

function Row({ row, setRows, requestAPI, showToast, openEdit }) {
    const [deleting, setDeleting] = useState();
    const remove = () => {
        requestAPI({
            requestPath: `website/gallery-media/${row.id}`,
            requestMethod: "DELETE",
            setLoading: setDeleting,
            parseResponseBody: false,
            onResponseReceieved: (_, responseCode) => {
                if (responseCode === 204) {
                    showToast({ severity: "success", summary: "Deleted", detail: "Removed", life: 1000 });
                    setRows((prev) => prev?.filter((item) => item.id !== row.id));
                } else showToast({ severity: "error", summary: "Failed", detail: "Failed To Delete !", life: 2000 });
            },
        });
    };
    return (
        <div className="flex gap-3 align-items-center border-round py-2 px-3 text-white bg-green-500">
            <div className="flex flex-column flex-1 overflow-hidden">
                <span className={`${TEXT_NORMAL} font-semibold`}>{row.title || (row.media_type === "video" ? "Video" : "Photo")}</span>
            </div>
            <i className="pi pi-pencil cursor-pointer" onClick={() => openEdit(row)} />
            <ProgressiveControl loading={deleting} control={<ConfirmationWrapper action={remove}><i className="pi pi-trash cursor-pointer" /></ConfirmationWrapper>} />
        </div>
    );
}

function Editor({ dialog, setDialog, setRows, requestAPI, showToast }) {
    const [record, setRecord] = useState({ media_type: "image", ...(dialog.record || {}) });
    const [saving, setSaving] = useState();
    const isNew = !record.id;
    const video = record.media_type === "video";
    const save = () => {
        if (!String(record.url || "").trim()) {
            showToast({ severity: "error", summary: "File required", detail: video ? "Upload the video" : "Upload the photo", life: 2000 });
            return;
        }
        requestAPI({
            requestPath: "website/gallery-media",
            requestMethod: isNew ? "POST" : "PATCH",
            requestPostBody: record,
            setLoading: setSaving,
            onRequestFailure: () => showToast({ severity: "error", summary: "Failed", detail: "Failed To Save !", life: 2000 }),
            onResponseReceieved: (saved, responseCode) => {
                if (saved && (responseCode === 200 || responseCode === 201)) {
                    setRows((prev) => (isNew ? [...(prev || []), saved] : prev.map((item) => (item.id === saved.id ? saved : item))));
                    showToast({ severity: "success", summary: "Saved", detail: video ? "Video saved" : "Photo saved", life: 1000 });
                    setDialog({ visible: false });
                } else showToast({ severity: "error", summary: "Failed", detail: saved?.error || "Failed To Save !", life: 2000 });
            },
        });
    };
    return (
        <Dialog header={isNew ? (video ? "Add a video" : "Add a photo") : (video ? "Edit video" : "Edit photo")} visible className="w-11" onHide={() => setDialog({ visible: false })}>
            <div className="flex flex-column">
                <WebsiteMediaInput label={video ? "Video file" : "Photo"} type={video ? "video" : "image"} field="url" hint={video ? "The video that plays when someone taps Play Video." : "Required size: 800 ? 800 px (square). A larger photo is scaled to fit this square. The whole photo stays visible."} record={record} setRecord={setRecord} disabled={saving} />
                {video ? <WebsiteMediaInput label="Video cover" field="thumbnail" hint="Required size: 800 ? 800 px (square). A larger picture is scaled to fit before the video plays. The whole picture stays visible." record={record} setRecord={setRecord} disabled={saving} /> : null}
                <div>
                    <FloatLabel className="mt-5">
                        <InputText id="title" className="w-full" value={record?.title ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, title: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                        <label htmlFor="title">{video ? "Video name" : "Photo name"}</label>
                    </FloatLabel>
                </div>
                <div>
                    <FloatLabel className="mt-5">
                        <InputTextarea id="caption" className="w-full" rows={3} autoResize value={record?.caption || ""} onChange={(event) => setRecord((prev) => ({ ...prev, caption: event.target.value }))} disabled={saving} />
                        <label htmlFor="caption">Caption</label>
                    </FloatLabel>
                    <FieldNote text="Optional line shown with this item." />
                </div>
                <div>
                    <FloatLabel className="mt-5">
                        <InputText id="alt_text" className="w-full" value={record?.alt_text ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, alt_text: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                        <label htmlFor="alt_text">Description for screen readers</label>
                    </FloatLabel>
                </div>
                {video ? (
                    <div>
                        <FloatLabel className="mt-5">
                            <InputText id="duration" className="w-full" value={record?.duration ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, duration: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                            <label htmlFor="duration">Length</label>
                        </FloatLabel>
                        <FieldNote text="Example: 2:30." />
                    </div>
                ) : null}
                <div className="flex align-items-center gap-2 mt-4">
                    <Checkbox inputId="downloadable" checked={!!record?.downloadable} onChange={(event) => setRecord((prev) => ({ ...prev, downloadable: event.checked }))} disabled={saving} />
                    <label htmlFor="downloadable" className={TEXT_SMALL}>Show a download button</label>
                </div>
                <Button className="mt-5 align-self-start" label="Save" icon="pi pi-check" loading={saving} onClick={save} />
            </div>
        </Dialog>
    );
}
